import { Box, HBox, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { IconLogout } from "./conponents/icons/IconLogout";
import { IconProfile } from "./conponents/icons/IconProfile";
import { getRoleName } from "src/permissions";

export const ProfileMenu = ({ collapsed = false, placement = "top" }: { collapsed?: boolean; placement?: "top" | "bottom" }) => {
	const { t } = useTranslation();
	const [user] = GD.REQ_PROFILE.useRequest();
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();
	const [isHovered, setIsHovered] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const closeTimerRef = useRef<number | null>(null);

	const keepMenuOpen = () => {
		if (closeTimerRef.current !== null) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
		setIsHovered(true);
	};

	const scheduleMenuClose = () => {
		if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
		closeTimerRef.current = window.setTimeout(() => {
			setIsHovered(false);
			closeTimerRef.current = null;
		}, 180);
	};

	useEffect(() => {
		return () => {
			if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
		};
	}, []);

	if (!user || !project) return null;

	const userRoles = user.projects
		.find(val => val.id === project.id)
		?.roles.map(val => val.name ?? getRoleName(val) ?? t("profile.role", { id: val.id ?? val.role_id ?? "unknown" }))
		.join(", ");

	const initials = user.name
		.split(" ")
		.map(w => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

	const handleLogout = () => {
		GD.S_AUTH_LOG_OUT.invoke();
		location.href = "/";
	};

	const handleProfile = () => {
		GD.S_PROFILE_POPUP.invoke({});
	};

	return (
		<div ref={triggerRef} style={{ position: "relative" }} onMouseEnter={keepMenuOpen} onMouseLeave={scheduleMenuClose}>
			<HBox
				center
				gap={collapsed ? "0px" : "12px"}
				sx={{
					width: "100%",
					minHeight: collapsed ? "36px" : "52px",
					padding: collapsed ? "7px 8px" : "6px 8px",
					borderRadius: "6px",
					cursor: "pointer",
					transition: "background-color 0.2s ease",
					justifyContent: collapsed ? "center" : "flex-start",
					"&:hover": {
						backgroundColor: "hsl(40 10% 91%)",
					},
				}}>
				<Box
					sx={{
						width: collapsed ? "18px" : "40px",
						height: collapsed ? "18px" : "40px",
						minWidth: collapsed ? "18px" : "40px",
						borderRadius: collapsed ? "50%" : "4px",
						background: collapsed ? "transparent" : "linear-gradient(135deg, #d85a26 0%, #7d1d8f 100%)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: collapsed ? "hsl(0 1% 37%)" : "#fff",
						fontSize: collapsed ? "12px" : "13px",
						fontWeight: 800,
						lineHeight: 1,
					}}>
					{collapsed ? <IconProfile size={18} color="hsl(0 1% 37%)" /> : initials}
				</Box>
				{!collapsed && (
					<>
						<VBox sx={{ minWidth: 0, flex: 1, gap: "1px" }}>
							<Typo
								sx={{
									color: "hsl(0 1% 37%)",
									fontSize: "12px",
									fontWeight: 800,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									lineHeight: 1.25,
								}}>
								{user.name}
							</Typo>
							<Typo
								sx={{
									color: "hsl(0 1% 37%)",
									opacity: 0.72,
									fontSize: "10px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									lineHeight: 1.25,
								}}>
								{userRoles}
							</Typo>
						</VBox>
						<ChevronDown
							size={16}
							color="hsl(0 1% 37%)"
							style={{
								flexShrink: 0,
								transform: isHovered ? "rotate(0deg)" : "rotate(180deg)",
								transition: "transform 0.2s ease",
							}}
						/>
					</>
				)}
			</HBox>

			{isHovered &&
				triggerRef.current &&
				(() => {
					const triggerRect = triggerRef.current.getBoundingClientRect();

					return (
						<div
							onMouseEnter={keepMenuOpen}
							onMouseLeave={scheduleMenuClose}
							style={{
								position: "fixed",
								bottom: placement === "top" ? `${window.innerHeight - triggerRect.top}px` : undefined,
								top: placement === "bottom" ? `${triggerRect.bottom}px` : undefined,
								left: `${collapsed ? triggerRect.right : triggerRect.left}px`,
								zIndex: 900,
								paddingBottom: placement === "top" && !collapsed ? "6px" : "0",
								paddingTop: placement === "bottom" ? "6px" : "0",
								paddingLeft: collapsed ? "8px" : "0",
							}}>
							<VBox
								sx={{
									backgroundColor: "#fff",
									borderRadius: "8px",
									boxShadow: "0 12px 28px rgba(61, 48, 40, 0.14), 0 2px 8px rgba(61, 48, 40, 0.08)",
									minWidth: "190px",
									overflow: "hidden",
									border: "1px solid hsl(30 10% 90%)",
								}}>
								<HBox
									gap="10px"
									sx={{
										alignItems: "center",
										justifyContent: "flex-start",
										padding: "11px 14px",
										cursor: "pointer",
										"&:hover": { backgroundColor: "hsl(40 10% 95%)" },
									}}
									onClick={handleProfile}>
									<IconProfile size={18} color="hsl(0 1% 37%)" />
									<Typo sx={{ color: "hsl(0 1% 37%)", fontSize: "13px", fontWeight: 700 }}>{t("profileMenu.profile")}</Typo>
								</HBox>
								<Box sx={{ height: "1px", backgroundColor: "hsl(30 10% 90%)", margin: "0 10px" }} />
								<HBox
									gap="10px"
									sx={{
										alignItems: "center",
										justifyContent: "flex-start",
										padding: "11px 14px",
										cursor: "pointer",
										"&:hover": { backgroundColor: "#fff0ed" },
									}}
									onClick={handleLogout}>
									<IconLogout size={18} color="#a6421c" />
									<Typo sx={{ color: "#a6421c", fontSize: "13px", fontWeight: 700 }}>{t("profileMenu.logout")}</Typo>
								</HBox>
							</VBox>
						</div>
					);
				})()}
		</div>
	);
};
